import { useEffect, useState } from "react"

import { AudioVideoFacade, VolumeIndicatorCallback } from "amazon-chime-sdk-js"

import EventEmitter from "src/EventEmitter"

export type RosterEntry = {
  attendeeId: string
  userId?: string
  userType?: string
  fullName?: string
  defaultLiveTranslationLanguageCode?: string | null
  defaultAudioPlaybackRate?: number
  muted?: boolean
  controlsLocked?: boolean
}

export type UserPayload = {
  action: string
  attendeeId: string
  userId: string
  userType: string
  fullName: string
  defaultLiveTranslationLanguageCode: string | null
  defaultAudioPlaybackRate: number
}

type UseRosterProps = {
  audioVideo: AudioVideoFacade | undefined
}

const useRoster = ({ audioVideo }: UseRosterProps) => {
  const [roster, setRoster] = useState<Record<string, RosterEntry>>({})

  // track attendee presence, and each attendee's mute state while present
  useEffect(() => {
    if (!audioVideo) {
      setRoster({})
      return
    }

    const subscribedAttendeeIds = new Set<string>()

    const handleVolumeChange: VolumeIndicatorCallback = (
      attendeeId,
      _volume,
      muted
    ) => {
      if (muted === null) return // null means unknown

      setRoster(currentRoster => {
        const existing = currentRoster[attendeeId]
        if (!existing || existing.muted === muted) return currentRoster

        return {
          ...currentRoster,
          [attendeeId]: { ...existing, muted },
        }
      })
    }

    const updateRosterOnAttendeePresence = (
      attendeeId: string,
      present: boolean,
      externalUserId?: string
    ) => {
      const [userType, userId] = externalUserId?.split("-") ?? []
      if (!userType || ["ghost", "aws:MediaPipeline"].includes(userType)) return

      setRoster(currentRoster => {
        const nextRoster = { ...currentRoster }
        if (present) {
          nextRoster[attendeeId] = {
            ...currentRoster[attendeeId],
            attendeeId,
            userId,
            userType,
          }
        } else {
          delete nextRoster[attendeeId]
        }
        return nextRoster
      })

      if (present) {
        if (!subscribedAttendeeIds.has(attendeeId)) {
          subscribedAttendeeIds.add(attendeeId)
          audioVideo.realtimeSubscribeToVolumeIndicator(
            attendeeId,
            handleVolumeChange
          )
        }
      } else {
        subscribedAttendeeIds.delete(attendeeId)
        audioVideo.realtimeUnsubscribeFromVolumeIndicator(
          attendeeId,
          handleVolumeChange
        )
      }
    }

    audioVideo.realtimeSubscribeToAttendeeIdPresence(
      updateRosterOnAttendeePresence
    )

    return () => {
      audioVideo.realtimeUnsubscribeToAttendeeIdPresence(
        updateRosterOnAttendeePresence
      )
      subscribedAttendeeIds.forEach(attendeeId => {
        audioVideo.realtimeUnsubscribeFromVolumeIndicator(
          attendeeId,
          handleVolumeChange
        )
      })
    }
  }, [audioVideo])

  // track attendee attributes passed from the backend (eg translation language)
  useEffect(() => {
    const onUserJoinedOrUpdated = (data: UserPayload) => {
      setRoster(currentRoster => {
        const existing = currentRoster[data.attendeeId]
        return {
          ...currentRoster,
          [data.attendeeId]: {
            ...existing,
            attendeeId: data.attendeeId,
            userId: data.userId,
            userType: data.userType,
            fullName: data.fullName,
            defaultLiveTranslationLanguageCode:
              data.defaultLiveTranslationLanguageCode,
            defaultAudioPlaybackRate: data.defaultAudioPlaybackRate,
          },
        }
      })
    }

    EventEmitter.subscribe("user_joined_or_updated", onUserJoinedOrUpdated)
    return () => {
      EventEmitter.unsubscribe("user_joined_or_updated", onUserJoinedOrUpdated)
    }
  }, [])

  return { roster }
}

export default useRoster
